import { useState } from 'react';
import type { ColDef, ICellRendererParams } from '../../../components/ui/table';
import CountryFlag from '../../../components/shared/CountryFlag';
import StatusBadge from '../../../components/shared/StatusBadge';
import {
  collectUniqueScalarValues,
} from '../../../components/shared/GridColumnFilterPanel';
import {
  GRID_CHIP_TONE,
  CopyableGridText,
  GridCellWrap,
  GridChip,
  EmailCopyCellRenderer,
  SubtitleCellRenderer,
  TerminalChipCellRenderer,
  CurrencyChipCellRenderer,
} from '../../../components/shared/agGridCells';
import { Button } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { copyToClipboard } from '../../../utils/clipboard';
import { formatAuditDateTime } from '../../../lib/auditColumnDefs';
import { formatCurrency } from '../../../utils/helpers';
import { getTransactionModeLabel } from '../../../utils/status';
import {
  BsArrowRepeat,
  BsArrowCounterclockwise,
  BsCreditCard2Back,
  BsBoxArrowUpRight,
  BsEye,
  BsGeoAltFill,
  BsLink45Deg,
  BsThreeDotsVertical,
} from 'react-icons/bs';
import type {
  TransactionColumnFilterKey,
  TransactionDccDetail,
  TransactionFilterLookups,
  TransactionGridContext,
  TransactionOrderRow,
} from '../models/types';
import type { MappedOrder } from '../utils/utils';

type TransactionFilterColumnConfig = {
  key: TransactionColumnFilterKey;
  label: string;
  getOptions: (rows: MappedOrder[]) => string[];
  adminOnly?: boolean;
  /** "dateRange" renders GridColumnFilterPanel's DateRangePicker instead of a checkbox list of
   *  exact values — used for "Txn Date" so the backend's real fromDate/toDate range query (not
   *  an exact-string match against every unique date seen in whatever rows happened to be
   *  loaded) actually powers the filter. */
  type?: 'select' | 'dateRange';
};

const TRANSACTION_FILTER_COLUMNS: TransactionFilterColumnConfig[] = [
  {
    key: 'merchantName',
    label: 'Merchant',
    adminOnly: true,
    // Options come from GET_MERCHANTS, not unique names on loaded orders.
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.merchantName || '—')),
  },
  {
    key: 'transactionId',
    label: 'Txn Id',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.transactionId || '—')),
  },
  {
    key: 'dateOnly',
    label: 'Txn Date',
    type: 'dateRange',
    getOptions: () => [],
  },
  {
    key: 'acquirerName',
    label: 'Acquirer',
    adminOnly: true,
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.acquirerName || '—')),
  },
  {
    key: 'acquirerTerminalName',
    label: 'Acquirer Terminal',
    adminOnly: true,
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.acquirerTerminalName || '—')),
  },
  {
    key: 'amount',
    label: 'Amount',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.amount ?? 0)),
  },
  {
    key: 'currency',
    label: 'Currency',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.currency || '—')),
  },
  {
    key: 'customerName',
    label: 'Customer',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.customerName || '—')),
  },
  {
    key: 'customerEmail',
    label: 'Email',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.customerEmail || '—')),
  },
  {
    key: 'country',
    label: 'Country',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.country || '—')),
  },
  {
    key: 'status',
    label: 'Status',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) =>
        String(row.rawStatus || row.status || '—'),
      ),
  },
  {
    key: 'mode',
    label: 'Mode',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => getTransactionModeLabel(row.testMode)),
  },
  {
    key: 'merchantTerminalName',
    label: 'Terminal',
    adminOnly: true,
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.merchantTerminalName || '—')),
  },
  {
    key: 'message',
    label: 'Message',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.message || '—')),
  },
];

export const TRANSACTION_COLUMN_LABELS = {
  ...Object.fromEntries(
    TRANSACTION_FILTER_COLUMNS.map(({ key, label }) => [key, label]),
  ),
  timeOnly: 'Time',
} as Record<TransactionColumnFilterKey, string>;

function columnLabel(key: TransactionColumnFilterKey) {
  return TRANSACTION_COLUMN_LABELS[key];
}

export function buildTransactionColumnFilterFields(
  sourceRows: MappedOrder[],
  isMerchantUser: boolean,
  lookups?: TransactionFilterLookups | null,
) {
  return TRANSACTION_FILTER_COLUMNS.filter(
    (column) => !column.adminOnly || !isMerchantUser,
  ).map(({ key, label, getOptions, type }) => {
    const lookupOptions =
      lookups && key in lookups
        ? lookups[key as keyof TransactionFilterLookups]
        : undefined;
    return {
      key,
      label,
      options: lookupOptions?.length ? lookupOptions : getOptions(sourceRows),
      type,
    };
  });
}

function ModeCellRenderer(params: ICellRendererParams<TransactionOrderRow>) {
  const row = params.data;
  if (!row) return null;
  return (
    <GridCellWrap>
      <StatusBadge status={row.testMode} variant="mode" />
    </GridCellWrap>
  );
}

function MerchantCellRenderer(params: ICellRendererParams<TransactionOrderRow>) {
  const row = params.data;
  if (!row) return null;
  return (
    <GridCellWrap>
      <span className="text-muted-foreground">{row.merchantName}</span>
      {row.isCheckout && (
        <GridChip label="Checkout" tone={GRID_CHIP_TONE.terminal} />
      )}
    </GridCellWrap>
  );
}

function TxnIdCellRenderer(params: ICellRendererParams<TransactionOrderRow>) {
  const row = params.data;
  const ctx = params.context as TransactionGridContext;
  if (!row) return null;

  const id = ctx.resolveRowTransactionId(row);
  if (!id) {
    return (
      <GridCellWrap>
        <span className="text-muted-foreground">—</span>
      </GridCellWrap>
    );
  }

  const display = id.length > 15 ? `${id.slice(0, 6)}...${id.slice(-4)}` : id;
  return (
    <GridCellWrap>
      <CopyableGridText
        text={display}
        copyValue={id}
        textClassName="text-muted-foreground"
        copyLabel="Copy Txn ID"
      />
    </GridCellWrap>
  );
}

function AmountCellRenderer(params: ICellRendererParams<TransactionOrderRow>) {
  const row = params.data;
  if (!row) return null;
  return (
    <GridCellWrap>
      <span className="text-muted-foreground">
        {formatCurrency(row.amount, row.currency)}
      </span>
    </GridCellWrap>
  );
}

function CustomerCellRenderer(params: ICellRendererParams<TransactionOrderRow>) {
  const row = params.data;
  if (!row) return null;
  return (
    <GridCellWrap>
      <span className="text-muted-foreground">{row.customerName}</span>
    </GridCellWrap>
  );
}

function CountryCellRenderer(params: ICellRendererParams<TransactionOrderRow>) {
  const row = params.data;
  if (!row) return null;
  return (
    <GridCellWrap>
      <span className="tm-country inline-flex items-center gap-2">
        {!row.countryFlag || row.countryFlag === '🏳️' || row.countryFlag.length !== 2 ? (
          <BsGeoAltFill className="opacity-50" />
        ) : (
          <CountryFlag
            countryFlag={row.countryFlag}
            countryId={row.countryId}
            countryName={row.country}
            className="tm-country-flag"
          />
        )}
        <span className="text-muted-foreground">{row.country}</span>
      </span>
    </GridCellWrap>
  );
}

function OrderStatusCellRenderer(params: ICellRendererParams<TransactionOrderRow, string>) {
  return (
    <GridCellWrap>
      <StatusBadge status={params.value} variant="order" />
    </GridCellWrap>
  );
}

type DccField = keyof Pick<
  TransactionDccDetail,
  'originalCurrency' | 'convertedCurrency' | 'originalAmount' | 'convertedAmount'
>;

function resolveDccFieldValue(dcc: TransactionDccDetail, field: DccField) {
  const value = dcc[field];
  if (field === 'originalAmount') {
    return formatCurrency(Number(value), dcc.originalCurrency);
  }
  if (field === 'convertedAmount') {
    return formatCurrency(Number(value), dcc.convertedCurrency);
  }
  return String(value);
}

/** No DCC conversion happened for this row — either no DccMarkupRate matched, or the detail just
 * hasn't loaded yet. Rather than leaving the DCC columns blank, show the transaction's own
 * currency/amount as both "base" and "converted": no conversion is exactly base === converted. */
function fallbackDccDetail(row: TransactionOrderRow): TransactionDccDetail {
  return {
    transactionId: row.transactionId,
    dccApplied: false,
    originalCurrency: row.currency,
    convertedCurrency: row.currency,
    originalAmount: row.amount,
    convertedAmount: row.amount,
  };
}

function currenciesMatch(a?: string | null, b?: string | null): boolean {
  return !!a && !!b && a.trim().toUpperCase() === b.trim().toUpperCase();
}

/** Shared by the cell renderer and the filter value getter below. If the acquirer terminal's
 * settlement currency matches the transaction's own currency, there's no real conversion no
 * matter what a markup rate/live FX lookup nominally produced — show base === converted just
 * like the no-DCC-applied case. Otherwise fall back to the existing dccApplied branching. */
function resolveDccDetail(
  realDcc: TransactionDccDetail | undefined,
  row: TransactionOrderRow,
): TransactionDccDetail {
  if (realDcc && currenciesMatch(realDcc.settlementCurrency, row.currency)) {
    return fallbackDccDetail(row);
  }
  return realDcc?.dccApplied ? realDcc : fallbackDccDetail(row);
}

function createDccCellRenderer(field: DccField) {
  return function DccCellRenderer(params: ICellRendererParams<TransactionOrderRow>) {
    const row = params.data;
    const ctx = params.context as TransactionGridContext;
    if (!row) {
      return (
        <GridCellWrap>
          <span className="text-muted-foreground">—</span>
        </GridCellWrap>
      );
    }

    const transactionId = ctx.resolveRowTransactionId(row);
    const realDcc = transactionId ? ctx.dccByTransactionId[transactionId] : undefined;
    const dcc = resolveDccDetail(realDcc, row);

    return (
      <GridCellWrap>
        <span className="text-muted-foreground">{resolveDccFieldValue(dcc, field)}</span>
      </GridCellWrap>
    );
  };
}

function MenuItemButton({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-foreground hover:bg-muted"
      onClick={onClick}
    >
      <span className="text-base">{icon}</span>
      {label}
    </button>
  );
}

// Merchants keep the plain row of icon buttons (few enough options, no menu needed). Admins get
// everything — View/Payment Link/Transaction Status/Resend Webhook plus the super-admin-only
// Mark as Refund/Chargeback — collapsed into a single three-dot menu, since that combined set is
// too many icons to show inline at once.
function TransactionActionsCellRenderer(params: ICellRendererParams<TransactionOrderRow>) {
  const row = params.data;
  const ctx = params.context as TransactionGridContext;
  const [menuOpen, setMenuOpen] = useState(false);
  if (!row) return null;

  const rowTransactionId = ctx.resolveRowTransactionId(row);

  if (ctx.isMerchantUser) {
    return (
      <div
        className="flex h-full flex-wrap items-center gap-1 py-1"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(3, max-content)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="tm-compact-icon-btn shrink-0"
              aria-label="View Transaction"
              onClick={() => ctx.onView(row)}
            >
              <BsEye />
            </Button>
          </TooltipTrigger>
          <TooltipContent>View Transaction</TooltipContent>
        </Tooltip>
        {row.checkoutUrl && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="tm-compact-icon-btn shrink-0"
                aria-label="Payment Link"
                onClick={() =>
                  copyToClipboard(row.checkoutUrl, 'Payment link copied to clipboard')
                }
              >
                <BsLink45Deg />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Payment Link</TooltipContent>
          </Tooltip>
        )}
        {row.merchantId && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="tm-compact-icon-btn shrink-0"
                aria-label="Resend Webhook"
                onClick={() => ctx.onResendWebhook(row)}
              >
                <BsArrowRepeat />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Resend Webhook</TooltipContent>
          </Tooltip>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full items-center py-1" onClick={(e) => e.stopPropagation()}>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="icon" className="tm-compact-icon-btn shrink-0" aria-label="Actions">
            <BsThreeDotsVertical />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-56 p-1">
          <MenuItemButton
            icon={<BsEye />}
            label="View Transaction"
            onClick={() => {
              setMenuOpen(false);
              ctx.onView(row);
            }}
          />
          {row.checkoutUrl && (
            <MenuItemButton
              icon={<BsLink45Deg />}
              label="Payment Link"
              onClick={() => {
                setMenuOpen(false);
                copyToClipboard(row.checkoutUrl, 'Payment link copied to clipboard');
              }}
            />
          )}
          {ctx.isSuperAdminUser && rowTransactionId && !row.isCheckout && (
            <MenuItemButton
              icon={<BsBoxArrowUpRight />}
              label="Transaction Status"
              onClick={() => {
                setMenuOpen(false);
                ctx.onOpenStatus(rowTransactionId);
              }}
            />
          )}
          {row.merchantId && (
            <MenuItemButton
              icon={<BsArrowRepeat />}
              label="Resend Webhook"
              onClick={() => {
                setMenuOpen(false);
                ctx.onResendWebhook(row);
              }}
            />
          )}
          {ctx.isSuperAdminUser && (
            <>
              <div className="my-1 h-px bg-border" />
              <MenuItemButton
                icon={<BsArrowCounterclockwise />}
                label="Mark as Refund"
                onClick={() => {
                  setMenuOpen(false);
                  ctx.onMarkRefund(row);
                }}
              />
              <MenuItemButton
                icon={<BsCreditCard2Back />}
                label="Mark as Chargeback"
                onClick={() => {
                  setMenuOpen(false);
                  ctx.onMarkChargeback(row);
                }}
              />
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

function MessageCellRenderer(params: ICellRendererParams<TransactionOrderRow, string>) {
  const message =
    params.value && params.value !== '—' ? String(params.value) : '—';

  if (message === '—') {
    return (
      <GridCellWrap>
        <span className="text-muted-foreground">—</span>
      </GridCellWrap>
    );
  }

  return (
    <GridCellWrap>
      <span className="block min-w-0 truncate">{message}</span>
    </GridCellWrap>
  );
}

function createDccFilterValueGetter(field: DccField) {
  return (params: { data?: TransactionOrderRow; context?: unknown }) => {
    const row = params.data;
    const ctx = params.context as TransactionGridContext;
    if (!row) return '—';

    const transactionId = ctx.resolveRowTransactionId(row);
    const realDcc = transactionId ? ctx.dccByTransactionId[transactionId] : undefined;
    const dcc = resolveDccDetail(realDcc, row);

    return resolveDccFieldValue(dcc, field);
  };
}

export function createTransactionColumnDefs(
  isMerchantUser: boolean,
  // Every plain ADMIN is a referral admin (see UserPrincipal.isReferralAdmin()) — they still see
  // the Merchant column (the whole point of the merchant filter), but not Email, Action, terminal,
  // or DCC detail.
  isReferralUser: boolean = false,
  isSuperAdminUser: boolean = false,
): ColDef<TransactionOrderRow>[] {
  const cols: ColDef<TransactionOrderRow>[] = [];

  if (!isMerchantUser) {
    cols.push({
      field: 'merchantName',
      headerName: columnLabel('merchantName'),
      flex: 1.5,
      minWidth: 240,
      cellRenderer: MerchantCellRenderer,
    });
  }

  cols.push(
    {
      field: 'transactionId',
      headerName: columnLabel('transactionId'),
      flex: 1,
      minWidth: 180,
      cellRenderer: TxnIdCellRenderer,
    },
    {
      // Date-only display; Time is the next column. colId stays dateOnly so AG Grid's
      // filterModel still maps to fromDate/toDate in getRows.
      colId: 'dateOnly',
      headerName: columnLabel('dateOnly'),
      width: 140,
      cellClass: 'text-muted-foreground',
      valueGetter: (params) => params.data?.createdAt ?? null,
      valueFormatter: (params) =>
        params.data?.dateOnly && params.data.dateOnly !== '—'
          ? params.data.dateOnly
          : formatAuditDateTime(String(params.value ?? '')),
      // Real date picker (not the default text "contains" filter) — restricted to the two
      // operators the backend can actually satisfy (a single day, or an inclusive range), since
      // its date filtering is a plain fromDate/toDate range query
      // (OrderSpecifications.inDateRange), not arbitrary before/after comparisons. Translated
      // into fromDate/toDate in the table datasource's getRows (TransactionMonitoring.tsx), same
      // pattern as the Mode column's native filter.
      filter: 'agDateColumnFilter',
      filterParams: {
        filterOptions: ['equals', 'inRange'],
        maxNumConditions: 1,
        buttons: ['clear', 'apply'],
        closeOnApply: true,
      },
    },
    {
      colId: 'timeOnly',
      field: 'timeOnly',
      headerName: columnLabel('timeOnly'),
      width: 110,
      cellClass: 'text-muted-foreground',
      sortable: false,
      filter: false,
    },
  );

  if (!isMerchantUser && !isReferralUser) {
    cols.push(
      {
        field: 'acquirerName',
        headerName: columnLabel('acquirerName'),
        flex: 1,
        minWidth: 180,
        cellRenderer: SubtitleCellRenderer,
      },
      {
        field: 'acquirerTerminalName',
        headerName: columnLabel('acquirerTerminalName'),
        flex: 1,
        minWidth: 220,
        cellRenderer: TerminalChipCellRenderer,
      },
    );
  }

  cols.push(
    {
      field: 'amount',
      headerName: columnLabel('amount'),
      width: 180,
      cellRenderer: AmountCellRenderer,
      filterValueGetter: (params) => String(params.data?.amount ?? ''),
    },
    {
      field: 'currency',
      headerName: columnLabel('currency'),
      width: 180,
      cellRenderer: CurrencyChipCellRenderer,
    },
    {
      field: 'customerName',
      headerName: columnLabel('customerName'),
      flex: 1,
      minWidth: 180,
      cellRenderer: CustomerCellRenderer,
    },
  );

  // Referral admins (AdminType.ADMIN) don't see customer email.
  if (!isReferralUser) {
    cols.push({
      field: 'customerEmail',
      headerName: columnLabel('customerEmail'),
      flex: 1.2,
      minWidth: 250,
      cellRenderer: EmailCopyCellRenderer,
    });
  }

  cols.push(
    {
      field: 'country',
      headerName: columnLabel('country'),
      flex: 1,
      minWidth: 180,
      cellRenderer: CountryCellRenderer,
    },
    {
      colId: 'status',
      field: 'transactionStatus',
      headerName: columnLabel('status'),
      width: 150,
      minWidth: 150,
      cellRenderer: OrderStatusCellRenderer,
      pinned: 'right',
    },
    {
      colId: 'mode',
      headerName: columnLabel('mode'),
      width: 120,
      minWidth: 120,
      cellRenderer: ModeCellRenderer,
      sortable: false,
      // The typed text here ("Sandbox"/"Live"/a prefix of either) is translated in the table
      // datasource's getRows (TransactionMonitoring.tsx) into the same request.mode field the
      // dedicated dropdown uses (OrderListRequest.mode / OrderSpecifications.hasMode) — not sent
      // as a generic dynamicFilters column, since "mode" isn't a real Order/Transaction column and
      // the backend's whitelist wouldn't recognize it.
      filterValueGetter: (params) => getTransactionModeLabel(params.data?.testMode),
      pinned: 'right',
    },
  );

  if (!isMerchantUser && !isReferralUser) {
    cols.push(
      {
        field: 'merchantTerminalName',
        headerName: columnLabel('merchantTerminalName'),
        flex: 1,
        minWidth: 180,
        cellRenderer: TerminalChipCellRenderer,
      },
      {
        colId: 'dccOriginalCurrency',
        headerName: 'Base Currency',
        width: 220,
        sortable: false,
        filterValueGetter: createDccFilterValueGetter('originalCurrency'),
        cellRenderer: createDccCellRenderer('originalCurrency'),
      },
      {
        colId: 'dccConvertedCurrency',
        headerName: 'Converted Currency',
        width: 220,
        sortable: false,
        filterValueGetter: createDccFilterValueGetter('convertedCurrency'),
        cellRenderer: createDccCellRenderer('convertedCurrency'),
      },
      {
        colId: 'dccOriginalAmount',
        headerName: 'Base Amount',
        width: 220,
        sortable: false,
        filterValueGetter: createDccFilterValueGetter('originalAmount'),
        cellRenderer: createDccCellRenderer('originalAmount'),
      },
      {
        colId: 'dccConvertedAmount',
        headerName: 'Converted Amount',
        width: 220,
        sortable: false,
        filterValueGetter: createDccFilterValueGetter('convertedAmount'),
        cellRenderer: createDccCellRenderer('convertedAmount'),
      },
    );
  }

  cols.push({
    field: 'message',
    headerName: columnLabel('message'),
    minWidth: 350,
    flex: 1.2,
    wrapText: false,
    autoHeight: false,
    suppressAutoSize: true,
    cellClass: 'tm-message',
    cellRenderer: MessageCellRenderer,
    tooltipValueGetter: (params) => {
      const value = params.value;
      if (!value || value === '—') return undefined;
      return String(value);
    },
    valueFormatter: (p) => (p.value && p.value !== '—' ? String(p.value) : '—'),
  });

  // Referral admins (AdminType.ADMIN) have no row actions on this grid.
  if (!isReferralUser) {
    cols.push({
      headerName: 'Action',
      // Super admin action cells are the single three-dot trigger (popover contents grow with
      // extra menu items, not the trigger). Merchants keep the inline icon row, so they need
      // the wider column.
      width: isSuperAdminUser ? 90 : 180,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: TransactionActionsCellRenderer,
    });
  }

  return cols;
}
